import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { IconCustomMenuDown, IconCustomMenuUp } from '@rossum/ui/icons/tabler';
import { Button, Collapse, Divider, Stack, SvgIcon } from '@rossum/ui/material';
import { Grid } from '@rossum/ui/system';
import { useGetSavedAutomationTargets } from '../../core/hooks/useGetSavedAutomationTargets';
import {
  convertErrorRateToPrecisionTarget,
  convertRateToPercentage,
} from '../../core/utils';
import {
  AggregationPeriod,
  CommonXAxisProps,
  TransformedAutomationInsights,
} from '../types';
import { getDateLocale, getPeriodBoundaries } from '../utils';
import { AutomationBlockersChart } from './charts/AutomationBlockersChart';
import { AutomationCountsChart } from './charts/AutomationCountsChart';
import { AutomationRateChart } from './charts/AutomationRateChart';
import { PrecisionRateChart } from './charts/PrecisionRateChart';
import {
  dateFormatter,
  filterOutInvalidDates,
  getAutomationBlockersChartData,
  getAutomationRateChartDataset,
  getPrecisionRateChartDataset,
} from './charts/utils';
import { PrecisionDisabledBanner } from './PrecisionDisabledBanner';

type AutomationChartsProps = {
  data: TransformedAutomationInsights;
  aggregationPeriod: AggregationPeriod;
  queueId: number;
  qualitySpotCheckEnabled: boolean;
};
export const AutomationCharts = ({
  data,
  aggregationPeriod,
  queueId,
  qualitySpotCheckEnabled,
}: AutomationChartsProps) => {
  const intl = useIntl();
  const [showMore, setShowMore] = useState(false);

  const {
    data: savedAutomationTargets,
    isLoading: isLoadingAutomationTargets,
  } = useGetSavedAutomationTargets({
    queueId,
  });

  const filteredData = useMemo(
    () => ({
      documentBlockers: data.documentBlockers,
      aggregatedDocumentAutomationTimeseries: filterOutInvalidDates(
        data.aggregatedDocumentAutomationTimeseries
      ),
      estimatedErrorRateTimeseries: filterOutInvalidDates(
        data.estimatedErrorRateTimeseries
      ),
      aggregatedEstimatedErrorRateTimeseries: filterOutInvalidDates(
        data.aggregatedEstimatedErrorRateTimeseries
      ),
    }),
    [data]
  );

  const { totalBlockers, automationRateChartDataset, totals } = useMemo(() => {
    const automationRateChartDataset = getAutomationRateChartDataset(
      filteredData.aggregatedDocumentAutomationTimeseries
    );

    const totalAutomated = R.sum(automationRateChartDataset.automatedCount);
    const totalDocs =
      totalAutomated + R.sum(automationRateChartDataset.nonAutomatedCount);

    const totals = {
      automationRate: totalDocs !== 0 ? totalAutomated / totalDocs : 0,
      totalAutomated,
      totalDocs,
    };

    return {
      totalBlockers: getAutomationBlockersChartData(
        filteredData.documentBlockers
      ),
      automationRateChartDataset,
      totals,
    };
  }, [
    filteredData.documentBlockers,
    filteredData.aggregatedDocumentAutomationTimeseries,
  ]);

  const { precisionRateChartDataset, averageTotalPrecision } = useMemo(() => {
    return {
      precisionRateChartDataset: getPrecisionRateChartDataset({
        aggregatedEstimatedErrorRateTimeseries:
          filteredData.aggregatedEstimatedErrorRateTimeseries,
        savedAutomationTargets,
      }),
      // days without an error rate estimate are left out of the average;
      // with no estimate at all we render the empty placeholder
      averageTotalPrecision: convertRateToPercentage(
        R.pipe(
          filteredData.estimatedErrorRateTimeseries,
          R.map(val => val.errorRateEstimate),
          R.filter(R.isNonNullish),
          R.map(convertErrorRateToPrecisionTarget),
          precisions => (precisions.length > 0 ? R.mean(precisions) : undefined)
        )
      ),
    };
  }, [
    filteredData.aggregatedEstimatedErrorRateTimeseries,
    filteredData.estimatedErrorRateTimeseries,
    savedAutomationTargets,
  ]);

  const periodBoundaries = useMemo(
    () => getPeriodBoundaries(data.documentAutomationTimeseries),
    [data.documentAutomationTimeseries]
  );
  const xAxisProps = useMemo<CommonXAxisProps>(() => {
    return {
      valueFormatter: (value, context) => {
        const locale = getDateLocale(intl.locale);
        const { startDate, endDate } = periodBoundaries;

        return dateFormatter({
          aggregationPeriod,
          value,
          locale,
          isTooltip: context.location === 'tooltip',
          startDate,
          endDate,
        });
      },
      tickLabelInterval:
        aggregationPeriod === 'weeks' ? (_, index) => index % 2 !== 0 : 'auto',
    };
  }, [aggregationPeriod, intl.locale, periodBoundaries]);

  return (
    <Stack sx={{ py: 3 }} spacing={3} useFlexGap>
      <Grid container spacing={3}>
        <Grid size={6}>
          <AutomationRateChart
            automationRateTotal={convertRateToPercentage(totals.automationRate)}
            automationRateChartDataset={automationRateChartDataset}
            xAxisProps={xAxisProps}
          />
        </Grid>
        <Grid size={6}>
          <AutomationCountsChart
            automationRateChartDataset={automationRateChartDataset}
            totals={totals}
            xAxisProps={xAxisProps}
          />
        </Grid>
        <Grid size={12}>
          <Collapse in={showMore}>
            <Grid container spacing={3}>
              <Grid size={6}>
                {qualitySpotCheckEnabled ? (
                  <PrecisionRateChart
                    isLoading={isLoadingAutomationTargets}
                    precisionRateChartDataset={precisionRateChartDataset}
                    aggregationPeriod={aggregationPeriod}
                    xAxisProps={xAxisProps}
                    averageTotalPrecision={averageTotalPrecision}
                  />
                ) : (
                  <PrecisionDisabledBanner queueId={queueId} />
                )}
              </Grid>
              <Grid size={6}>
                <AutomationBlockersChart totalBlockers={totalBlockers} />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
        <Grid size={12}>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
            }}
            spacing={2}
            useFlexGap
          >
            <Divider sx={{ flex: 1 }} />
            <Button
              sx={{
                position: 'relative',
                '&:not(:hover)': {
                  backgroundColor: t => t.palette.background.default,
                },
              }}
              color="secondary"
              onClick={() => setShowMore(!showMore)}
              startIcon={
                <SvgIcon fontSize="small">
                  {showMore ? <IconCustomMenuUp /> : <IconCustomMenuDown />}
                </SvgIcon>
              }
              data-cy="automation-settings-more-reports-button"
            >
              {showMore
                ? intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.charts.showLess',
                  })
                : intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.charts.showMore',
                  })}
            </Button>
            <Divider sx={{ flex: 1 }} />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
